/**
 * SEOHead.jsx
 * Reusable Helmet wrapper for dynamic <head> meta tags.
 * Provides: title, description, OG, Twitter Card, canonical, JSON-LD.
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "TheScope180";
const DEFAULT_OG_IMAGE = "https://www.thescope180.com/og/default.png";
const BASE_URL = "https://www.thescope180.com";

export default function SEOHead({
  title,
  description,
  ogImage,
  path,
  type = "website",
  jsonLd,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = path ? `${BASE_URL}${path}` : undefined;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@thescope180" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* JSON-LD Structured Data (supports single object or array) */}
      {jsonLd && Array.isArray(jsonLd)
        ? jsonLd.map((ld, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
          ))
        : jsonLd && (
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          )}
    </Helmet>
  );
}
